
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export const useAIChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [popularQuestions, setPopularQuestions] = useState<string[]>([]);

  const saveQuestion = async (question: string) => {
    try {
      // Generate a similarity group using basic text normalization
      const similarityGroup = question.toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(' ')
        .slice(0, 3)
        .join(' ');

      // Insert or update the question - fixed the upsert options
      const { data, error } = await supabase
        .from('ai_chat_questions')
        .upsert({
          question,
          page_context: window.location.pathname,
          similarity_group: similarityGroup,
        }, {
          onConflict: 'question'
        });

      if (error) {
        console.error('Error saving question:', error);
      }
    } catch (error) {
      console.error('Unexpected error saving question:', error);
    }
  };

  const findPopularQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_chat_questions')
        .select('question')
        .order('popularity_score', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching popular questions:', error);
        return;
      }

      setPopularQuestions(data?.map(q => q.question) || []);
    } catch (error) {
      console.error('Unexpected error finding popular questions:', error);
    }
  };

  const sendMessage = async (userMessage: string) => {
    // Create a properly typed message object
    const userMessageObj: Message = { role: 'user', content: userMessage };
    const newMessages = [...messages, userMessageObj];
    setMessages(newMessages);
    setIsLoading(true);

    // Save the question
    await saveQuestion(userMessage);

    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { 
          messages: newMessages,
          context: {
            currentPage: window.location.pathname,
            userAgent: navigator.userAgent
          }
        },
      });

      if (error) {
        throw new Error('AI response failed: ' + error.message);
      }

      const assistantMessage: Message = { role: 'assistant', content: data.message };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to get AI response');
      console.error(error);
    } finally {
      setIsLoading(false);
      // Fetch popular questions after sending a message
      await findPopularQuestions();
    }
  };

  return { 
    messages, 
    sendMessage, 
    isLoading, 
    popularQuestions 
  };
};
